/**
 * Sign canonical JSON with the host AIK for attestation connect / machine-owner proof.
 * Host app is a client only — this never decides ownership; realtime-api does.
 */

import { canonicalJson, generators } from '@playcastdotio/Messaging';
import { ipcSend } from '../messaging/protocols/ipc';
import { signMachineIdPayloadReplyState } from '../stores/signMachineIdPayloadReplyStore';

export type CanonicalJsonSigner = (canonicalJson: string) => Promise<string>;

/** Alias for shared Messaging.canonicalJson (AIK proof signing). */
export const createCanonicalJson = canonicalJson;

const SIGN_TIMEOUT_MS = 10_000;

let signerOverride: CanonicalJsonSigner | null = null;

/** Test-only: inject a signer (e.g. mock). Pass null to clear. */
export const setCanonicalJsonSignerForTests = (signer: CanonicalJsonSigner | null): void => {
  signerOverride = signer;
};

const newRequestId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `sign_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

async function signViaPlayjector(canonicalJson: string): Promise<string> {
  const requestId = newRequestId();

  return new Promise((resolve, reject) => {
    let listenerId: string | null = null;

    const timeout = setTimeout(() => {
      if (listenerId) {
        signMachineIdPayloadReplyState.removeListener(listenerId);
      }
      reject(new Error('Timeout waiting for AIK signature from Playjector'));
    }, SIGN_TIMEOUT_MS);

    listenerId = signMachineIdPayloadReplyState.addListener((reply) => {
      if (!reply || reply.requestId !== requestId) {
        return;
      }

      clearTimeout(timeout);
      if (listenerId) {
        signMachineIdPayloadReplyState.removeListener(listenerId);
      }

      if (reply.errorCode !== 'SUCCESS') {
        reject(new Error(`AIK sign failed: ${reply.error || reply.errorCode}`));
        return;
      }

      if (typeof reply.signature !== 'string' || reply.signature.length === 0) {
        reject(new Error('AIK sign reply missing signature'));
        return;
      }

      resolve(reply.signature);
    });

    ipcSend(generators.system.signMachineIdPayload(canonicalJson, requestId));
  });
}

/**
 * Sign canonical JSON bytes with the TPM AIK via Playjector SignWithAik IPC.
 */
export async function signCanonicalJson(canonicalJson: string): Promise<string> {
  if (signerOverride) {
    return signerOverride(canonicalJson);
  }
  return signViaPlayjector(canonicalJson);
}
